import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { meetingApi } from "../api/meetingApi";
import { Meeting } from "../types/supabase";

export function useMeetings() {
  return useQuery({
    queryKey: ["meetings"],
    queryFn: () => meetingApi.getAll(),
  });
}

export function useMeeting(id: string) {
  return useQuery({
    queryKey: ["meeting", id],
    queryFn: () => meetingApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Meeting>) => meetingApi.insert(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["meetings"] }),
  });
}

export function useUpsertMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Meeting>) => meetingApi.upsert(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
      if (data?.uuid) {
        queryClient.invalidateQueries({ queryKey: ["meeting", data.uuid] });
      }
    },
  });
}

export function useMeetingRealtime(onChange: (payload: any) => void) {
  useEffect(() => {
    const subscription = meetingApi.subscribe(onChange);
    return () => {
      subscription.unsubscribe();
    };
  }, [onChange]);
}

export function useLiveMeeting(sessionId: string) {
  const query = useMeeting(sessionId);

  useEffect(() => {
    if (!sessionId) return;
    const subscription = meetingApi.subscribeById(sessionId, () => {
      query.refetch();
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [sessionId, query]);

  return query;
}
